import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDownRight, ArrowUpRight, CalendarDays, Check, Instagram, Mail, MapPin, MessageCircle, Plus } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { useSiteContent, useMedia } from "../lib/content";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { RevealLines, Rise, LineRise } from "../components/Reveal";
import { api } from "../lib/api";
import { formatApiError, useAuth } from "../lib/AuthContext";
import { validatePhone } from "../lib/validate";
import { PhoneField } from "../components/PhoneField";
import { useT, Editable } from "../lib/i18n";
import ClassesRSVP from "../components/ClassesRSVP";

const TRAININGS = [
  { id: "boxing101", label: "Boxing 101", key: "training.boxing101.b", tag: "beginner", img: "home.training.boxing101", duration: "60 MIN" },
  { id: "boxing", label: "Boxing Training", key: "training.boxing.b", tag: "all levels", img: "home.training.boxing", duration: "90 MIN" },
  { id: "bag", label: "Bag Training", key: "training.bag.b", tag: "conditioning", img: "home.training.bag", duration: "60 MIN" },
  { id: "sparring", label: "Sparring", key: "training.sparring.b", tag: "advanced", img: "home.training.sparring", duration: "90 MIN" },
  { id: "pt", label: "Personal Training", key: "training.pt.b", tag: "1:1", img: "home.training.pt", duration: "60 MIN" },
];

const SCHEDULE = [
  { day: "Dinsdag", dayEn: "Tuesday", time: "18:30 — 20:00", type: "Boxing Training", level: "All levels", coach: "Glenn" },
  { day: "Donderdag", dayEn: "Thursday", time: "18:30 — 20:00", type: "Boxing Training", level: "All levels", coach: "Glenn" },
  { day: "Vrijdag", dayEn: "Friday", time: "10:00 — 11:00", type: "Bag Training", level: "Conditioning", coach: "Glenn" },
  { day: "Zondag", dayEn: "Sunday", time: "09:00 — 10:00", type: "Boxing 101", level: "Beginner", coach: "Glenn" },
  { day: "Zondag", dayEn: "Sunday", time: "10:30 — 12:00", type: "Sparring", level: "Advanced", coach: "Glenn" },
];

const VISION_KEYS = [
  { n: "01", labelK: "vision.1.t", textK: "vision.1.b" },
  { n: "02", labelK: "vision.2.t", textK: "vision.2.b" },
  { n: "03", labelK: "vision.3.t", textK: "vision.3.b" },
  { n: "04", labelK: "vision.4.t", textK: "vision.4.b" },
  { n: "05", labelK: "vision.5.t", textK: "vision.5.b" },
];

/* --------------------------------- HERO --------------------------------- */
function Hero() {
  const { t } = useT();
  const { content } = useSiteContent();
  const media = useMedia();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.08]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.4]);

  return (
    <section id="hero" className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      {/* BG */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          src={media("site.hero_video")}
          poster={media("site.hero_poster")}
          autoPlay
          loop
          muted
          playsInline
          data-testid="hero-video"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.25) 40%, rgba(10,10,10,0.75) 100%)" }} />
      </motion.div>

      {/* Top meta */}
      <motion.div style={{ opacity }} className="relative z-10 pt-28 md:pt-32 px-6 md:px-10">
        <div className="flex items-center justify-between text-[#E8E4DF] chip">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-[#E8E4DF] rounded-full animate-pulse" />
            <span>{t("hero.meta.est")}</span>
          </div>
        </div>
      </motion.div>

      {/* Title */}
      <div className="relative z-10 px-6 md:px-10 pt-16 md:pt-24">
        <h1 className="font-display uppercase text-[#F5F3F0] text-[16vw] md:text-[13vw] lg:text-[11.5vw]" data-testid="hero-title">
          <RevealLines
            lines={[<Editable k="hero.h1.1">Learn how</Editable>, <Editable k="hero.h1.2">to <span className="font-serif-italic normal-case tracking-tight accent-ink">box.</span></Editable>, <Editable k="hero.h1.3">Step into</Editable>, <Editable k="hero.h1.4">your <span className="accent-ink">potential.</span></Editable>]}
            delay={0.35}
            lineDelay={0.14}
          />
        </h1>
      </div>
    </section>
  );
}

/* --------------------------------- MARQUEE --------------------------------- */
function EditorialMarquee() {
  const words = ["Fundamentals", "Progression", "Community", "Discipline", "Amsterdam"];
  return (
    <div className="bg-[#F5F3F0] line-t line-b py-8 md:py-10 overflow-hidden" data-testid="marquee">
      <Marquee speed={40} gradient={false} pauseOnHover>
        {words.concat(words).map((w, i) => (
          <span key={i} className="flex items-center">
            <span className={`font-display uppercase text-[10vw] md:text-[7vw] leading-none px-8 ${i % 2 === 0 ? "text-[#0A0A0A]" : "outline-text text-[#0A0A0A]"}`}>{w}</span>
            <span className="text-[#0A0A0A] font-display text-[6vw]">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* --------------------------------- CLASSES TEASER --------------------------------- */
function ClassesTeaser() {
  const { t, lang } = useT();
  const { content } = useSiteContent();
  const teaser = content?.teaser;
  const teaserSub = (teaser && (lang === "en" ? teaser.sub_en : teaser.sub_nl)) || t("teaser.sub");
  const KIND_LABEL = { training: { nl: "Training", en: "Training" }, event: { nl: "Event", en: "Event" }, mededeling: { nl: "Mededeling", en: "Notice" }, activiteit: { nl: "Activiteit", en: "Activity" } };
  const baseCards = (teaser?.cards?.length
    ? teaser.cards
    : (content?.schedule?.rows?.length ? content.schedule.rows : SCHEDULE)
  ).map((s) => ({ ...s, time: s.time || `${s.start} — ${s.end}` }));
  const extraCards = (content?.week_items || []).map((w) => {
    const d = new Date(w.date + "T00:00:00");
    return {
      day: d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "short" }),
      dayEn: d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" }),
      time: w.time || "",
      type: w.title,
      level: (KIND_LABEL[w.kind] || KIND_LABEL.event)[lang === "en" ? "en" : "nl"],
      desc: w.description || "",
    };
  });
  const cards = [...baseCards, ...extraCards];
  return (
    <section className="bg-[#F5F3F0] line-b py-20 md:py-28" data-testid="classes-teaser-section">
      <div className="px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="section-tag">{t("teaser.ch")}</div>
            <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl text-[#0A0A0A] leading-[0.9] mt-4" data-testid="teaser-title">
              <LineRise><Editable k="teaser.h1">The <span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]">classes.</span></Editable></LineRise>
            </h2>
            <p className="text-sm text-[#4A4A4A] mt-4 max-w-md" data-testid="teaser-sub">{teaserSub}</p>
          </div>
          <button
            onClick={() => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-ghost hidden md:inline-flex"
            data-testid="teaser-view-schedule-btn"
          >
            {t("teaser.btn")} <ArrowUpRight size={14} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {cards.map((s, i) => (
            <Rise key={i} delay={i * 0.06}>
              <div className="group border border-[#0A0A0A] p-5 md:p-6 h-full flex flex-col justify-between bg-[#EAE7E2] hover:bg-[#0A0A0A] hover:text-[#F5F3F0] transition-colors duration-300 cursor-pointer" data-testid={`teaser-card-${i}`} onClick={() => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" })} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" }); }}>
                <div>
                  <div className="chip text-[#4A4A4A] group-hover:text-[#A3A3A3]">{lang === "en" ? s.dayEn : s.day}</div>
                  <div className="font-mono text-xs mt-1 text-[#4A4A4A] group-hover:text-[#DDD6CB]">{s.time}</div>
                </div>
                <div className="mt-8">
                  <div className="font-display uppercase text-2xl md:text-3xl leading-[0.95]">{s.type}</div>
                  <div className="chip text-[#4A4A4A] group-hover:text-[#A3A3A3] mt-2">{s.level}</div>
                  {s.desc && <div className="text-xs text-[#4A4A4A] group-hover:text-[#DDD6CB] mt-2 leading-snug">{s.desc}</div>}
                </div>
              </div>
            </Rise>
          ))}
        </div>

        <div className="mt-6 md:hidden">
          <button
            onClick={() => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-ghost w-full justify-center"
            data-testid="teaser-view-schedule-btn-mobile"
          >
            {t("teaser.btn")} <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- INTRO --------------------------------- */
function Intro() {
  const { t } = useT();
  return (
    <section className="relative py-32 md:py-48 px-6 md:px-10" data-testid="intro-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2 section-tag">{t("intro.ch")}</div>
        <div className="lg:col-span-10">
          <h2 className="font-display uppercase text-[#0A0A0A] text-[13vw] lg:text-[9vw] leading-[0.86]" data-testid="intro-title">
            <LineRise><Editable k="intro.h1.1">Boxing is the</Editable></LineRise>
            <LineRise delay={0.1}>
              <span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]"><Editable k="intro.h1.2">starting</Editable></span>
            </LineRise>
            <LineRise delay={0.2}><Editable k="intro.h1.3">point.</Editable></LineRise>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl">
            <Rise delay={0.1}>
              <p className="text-[#0A0A0A] text-lg leading-relaxed">{t("intro.p1")}</p>
            </Rise>
            <Rise delay={0.2}>
              <p className="text-[#4A4A4A] text-lg leading-relaxed">{t("intro.p2")}</p>
            </Rise>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- VALUES --------------------------------- */
function Values() {
  const { t } = useT();
  const items = [
    { n: "R", title: "Respect", key: "values.1" },
    { n: "D", title: "Discipline", key: "values.2" },
    { n: "U", title: "Unity", key: "values.3" },
  ];
  return (
    <section className="bg-[#EAE7E2] py-24 md:py-40 line-t line-b" data-testid="values-section">
      <div className="px-6 md:px-10">
        <div className="mb-12 max-w-3xl">
          <div className="section-tag mb-4">— Values / R.D.U.</div>
          <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-9xl text-[#0A0A0A] leading-[0.86]" data-testid="values-title">R.D.U.</h2>
          <p className="mt-4 font-display uppercase text-2xl md:text-3xl text-[#4A4A4A]">Respect. Discipline. Unity.</p>
          <p className="mt-6 text-[#0A0A0A] text-base md:text-lg leading-relaxed">
            Respect bepaalt hoe we met elkaar omgaan. Discipline bepaalt hoe we trainen. Unity zorgt ervoor dat niemand het alleen hoeft te doen.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {items.map((v, i) => (
            <Rise key={v.n} delay={i * 0.1}>
              <div className="border-2 h-full p-6 md:p-8 bg-[#F5F3F0]" style={{borderColor: "var(--joh-accent)"}}>
                <div className="font-display text-6xl md:text-7xl accent-ink" style={{color: "var(--joh-accent)"}}>{v.n}</div>
                <h3 className="font-display uppercase text-3xl md:text-4xl text-[#0A0A0A] mt-2">{v.title}</h3>
                <p className="mt-4 text-[#0A0A0A] text-base leading-relaxed">{t(v.key)}</p>
              </div>
            </Rise>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- TRAININGS --------------------------------- */
function Trainings() {
  const { t } = useT();
  const media = useMedia();
  const [hover, setHover] = useState(null);
  return (
    <section id="trainings" className="py-32 md:py-48 px-6 md:px-10 relative" data-testid="trainings-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
        <h2 className="lg:col-span-12 font-display uppercase text-[13vw] lg:text-[8.5vw] text-[#0A0A0A] leading-[0.86]" data-testid="trainings-title">
          <LineRise><Editable k="values.h1.1">Find your way</Editable></LineRise>
          <LineRise delay={0.1}><span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]"><Editable k="values.h1.2">into</Editable></span></LineRise>
          <LineRise delay={0.2}><Editable k="values.h1.3">boxing.</Editable></LineRise>
        </h2>
      </div>

      <div className="line-t">
        {TRAININGS.map((tr, i) => (
          <Rise key={tr.id} delay={i * 0.05}>
            <div
              className="reveal-row line-b py-8 md:py-10 flex items-center justify-between gap-6 cursor-default"
              onMouseEnter={() => setHover(tr.id)}
              onMouseLeave={() => setHover(null)}
              data-testid={`training-row-${tr.id}`}
            >
              <div className="flex items-center gap-6 md:gap-10 flex-1 min-w-0">
                <span className="chip text-[#4A4A4A] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display uppercase text-4xl md:text-6xl text-[#0A0A0A] shrink-0">
                  {tr.label}
                </h3>
                <p className="hidden md:block text-[#4A4A4A] max-w-md text-sm leading-relaxed truncate">
                  {t(tr.key)}
                </p>
              </div>
              <div className="flex items-center gap-6 shrink-0">
                <span className="chip text-[#4A4A4A] hidden md:block">{tr.tag}</span>
                <span className="chip text-[#0A0A0A]">{tr.duration}</span>
                <ArrowUpRight size={18} className="text-[#0A0A0A]" />
              </div>
              <img src={media(tr.img)} alt="" className="thumb hidden md:block" />
            </div>
          </Rise>
        ))}
      </div>

      <div className="mt-10 md:hidden text-[#4A4A4A] text-sm">
        {hover ? t(TRAININGS.find(x => x.id === hover)?.key) : ""}
      </div>
    </section>
  );
}

/* --------------------------------- SCHEDULE --------------------------------- */
function Schedule() {
  const { t, lang } = useT();
  const { content } = useSiteContent();
  const { user } = useAuth();
  const rows = (content?.schedule?.rows?.length
    ? content.schedule.rows.map((r) => ({ ...r, time: `${r.start} — ${r.end}` }))
    : SCHEDULE);
  const ann = content?.announcement;
  const annText = ann?.active ? (lang === "en" ? ann.text_en : ann.text_nl) : null;
  const annDate = ann?.date
    ? new Date(ann.date + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { day: "numeric", month: "long", year: "numeric" })
    : "";
  return (
    <section id="schedule" className="bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark py-32 md:py-40" data-testid="schedule-section">
      <div className="px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-2 section-tag section-tag-dark">{t("schedule.ch")}</div>
          <div className="lg:col-span-10">
            <h2 className="font-display uppercase text-[13vw] lg:text-[8.5vw] leading-[0.86]" data-testid="schedule-title">
              <LineRise><Editable k="schedule.h1.1">The <span className="font-serif-italic normal-case tracking-tight text-[#DDD6CB]">week</span></Editable></LineRise>
              <LineRise delay={0.1}><Editable k="schedule.h1.2">at JOH.</Editable></LineRise>
            </h2>
          </div>
        </div>

        <div className="line-t line-dark">
          <div className="hidden md:grid grid-cols-12 line-b line-dark py-4 chip text-[#A3A3A3]">
            <div className="col-span-2">{t("schedule.day")}</div>
            <div className="col-span-3">{t("schedule.time")}</div>
            <div className="col-span-4">{t("schedule.type")}</div>
            <div className="col-span-2">{t("schedule.level")}</div>
            <div className="col-span-1 text-right">{t("schedule.coach")}</div>
          </div>
          {rows.map((s, i) => {
            const tip = {
              "Boxing Training": "Complete groepsles: techniek, combinaties, pads en conditie.",
              "Bag Training": "Energieke bokszak-sessie voor tempo, kracht en conditie.",
              "Boxing 101": "Toegankelijke basisles voor beginners.",
              "Sparring": "Technische, gecontroleerde sparring-sessies.",
            }[s.type] || "";
            return (
            <Rise key={i} delay={i * 0.05}>
              <div className="line-b line-dark grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-0 py-6 md:py-7 items-center relative group cursor-help" title={tip} data-testid={`schedule-row-${i}`}>
                <div className="md:col-span-2 font-display text-2xl md:text-3xl uppercase">{lang === "en" ? s.dayEn : s.day}</div>
                <div className="md:col-span-3 font-mono text-sm md:text-base text-[#DDD6CB]">{s.time}</div>
                <div className="md:col-span-4 text-lg md:text-2xl">{s.type}</div>
                <div className="md:col-span-2 chip text-[#A3A3A3]">{s.level}</div>
                <div className="md:col-span-1 text-right chip text-[#DDD6CB]">{s.coach}</div>
                {tip && (
                  <div className="pointer-events-none absolute left-4 md:left-[41%] top-full mt-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 max-w-xs bg-[#F5F3F0] text-[#0A0A0A] px-4 py-3 text-sm shadow-lg" data-testid={`schedule-tooltip-${i}`}>
                    <div className="chip text-[#4A4A4A] mb-1">{s.type}</div>
                    {tip}
                  </div>
                )}
              </div>
            </Rise>
          );})}
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-sm text-[#A3A3A3] max-w-lg">{t("schedule.note")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <button className="btn-light" onClick={() => user?.email ? document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth" }) : window.location.assign(`/login?next=${encodeURIComponent("/#rsvp")}`)} data-testid="schedule-reserve-btn">
              {t("schedule.btn")} <ArrowUpRight size={14} />
            </button>
            {annText && (
              ann.url ? (
                <Link to={ann.url} className="chip px-4 py-3 border inline-flex items-center gap-3 transition-colors hover:bg-[#D4A93A]/10" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="home-announcement-button">
                  <CalendarDays size={14} />
                  <span>{annText}</span>
                  {annDate && <span className="text-[#DDD6CB]">· {annDate}</span>}
                </Link>
              ) : (
                <span className="chip px-4 py-3 border inline-flex items-center gap-3" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="home-announcement-button">
                  <CalendarDays size={14} />
                  <span>{annText}</span>
                  {annDate && <span className="text-[#DDD6CB]">· {annDate}</span>}
                </span>
              )
            )}
          </div>
        </div>

        {/* Wat heb je nodig? — compact info-blok onder het rooster */}
        <Rise delay={0.1}>
          <div className="mt-12 max-w-4xl rounded-3xl border border-[#F5F3F0]/15 bg-[#F5F3F0]/[0.04] p-6 md:p-8" data-testid="schedule-gear-card">
            <h3 className="font-display uppercase text-2xl md:text-3xl accent-ink" data-testid="schedule-gear-title">
              {lang === "en" ? "What do you need?" : "Wat heb je nodig?"}
            </h3>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <div>
                <div className="chip text-[#A3A3A3] mb-4">{lang === "en" ? "Bring your own training gear" : "Neem je eigen trainingsmateriaal mee"}</div>
                <ul className="space-y-2.5">
                  {(lang === "en"
                    ? ["Boxing gloves", "Hand wraps", "Sportswear", "Clean trainers or boxing shoes", "Towel", "Water bottle"]
                    : ["Bokshandschoenen", "Bandages / hand wraps", "Sportkleding", "Schone sportschoenen of boksschoenen", "Handdoek", "Waterfles"]
                  ).map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-[#DDD6CB]" data-testid={`schedule-gear-required-${i}`}>
                      <Check size={14} strokeWidth={2} className="shrink-0" style={{ color: "var(--joh-accent)" }}/>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="chip text-[#A3A3A3] mb-4">{lang === "en" ? "Recommended / if applicable" : "Aanbevolen / indien van toepassing"}</div>
                <ul className="space-y-2.5">
                  {(lang === "en" ? ["Mouthguard", "Groin protector", "Sports bra"] : ["Bitje", "Tok / kruisbeschermer", "Sport-bh"]).map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-[#DDD6CB]" data-testid={`schedule-gear-recommended-${i}`}>
                      <Plus size={14} strokeWidth={2} className="shrink-0 text-[#A3A3A3]"/>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-7 pt-6 border-t border-[#F5F3F0]/10">
              <div className="chip text-[#A3A3A3] mb-2">{lang === "en" ? "Own gear" : "Eigen materiaal"}</div>
              <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl" data-testid="schedule-gear-note">
                {lang === "en"
                  ? "At JOH. we expect you to bring your own training gear as much as possible. Keep it clean and in good condition — good hygiene matters, for you and for the people you train with."
                  : "Bij JOH. verwachten we dat je zoveel mogelijk je eigen trainingsmateriaal meeneemt. Zorg ervoor dat je materiaal schoon en in goede staat is. Goede hygiëne is belangrijk voor jezelf én voor de mensen met wie je traint."}
              </p>
            </div>
          </div>
        </Rise>
      </div>
    </section>
  );
}

/* --------------------------------- MEMBERSHIP --------------------------------- */
const MEMBERSHIP_ITEMS = [
  {
    key: "joh_membership_1x",
    price: "€40",
    period: { nl: "p/m", en: "p/m" },
    label: { nl: "1× per week", en: "1× per week" },
    title: { nl: "1× per week", en: "1× per week" },
    bullets: {
      nl: ["1 groepsles per week", "Alle lestypes op het rooster", "Maandelijks opzegbaar"],
      en: ["1 group class per week", "All class types on the schedule", "Cancel monthly"],
    },
    note: { nl: "Perfect voor een rustige start", en: "Perfect for a calm start" },
    cta: { nl: "Kies dit plan", en: "Choose plan" },
    popular: false,
    hasIntake: true,
  },
  {
    key: "joh_membership_2x",
    price: "€65",
    period: { nl: "p/m", en: "p/m" },
    label: { nl: "2× per week", en: "2× per week" },
    title: { nl: "2× per week", en: "2× per week" },
    bullets: {
      nl: ["2 groepslessen per week", "Alle lestypes op het rooster", "Maandelijks opzegbaar"],
      en: ["2 group classes per week", "All class types on the schedule", "Cancel monthly"],
    },
    note: { nl: "De sweet spot voor consistente progressie", en: "The sweet spot for consistent progression" },
    cta: { nl: "Kies dit plan", en: "Choose plan" },
    popular: true,
    hasIntake: true,
  },
  {
    key: "joh_membership_3x",
    price: "€80",
    period: { nl: "p/m", en: "p/m" },
    label: { nl: "Onbeperkt", en: "Unlimited" },
    title: { nl: "3× per week", en: "3× per week" },
    bullets: {
      nl: ["Onbeperkt groepslessen per week", "Toegang tot alle lestypes", "Maandelijks opzegbaar"],
      en: ["Unlimited group classes per week", "Access to every class type", "Cancel monthly"],
    },
    note: { nl: "Voor wie serieus wilt groeien — train zo vaak als je wilt", en: "For those who want to grow seriously — train as often as you like" },
    cta: { nl: "Kies dit plan", en: "Choose plan" },
    popular: false,
    hasIntake: true,
  },
  {
    key: "joh_pack_5",
    price: "€60",
    period: { nl: "5 lessen · 3 mnd geldig", en: "5 classes · valid 3 months" },
    label: { nl: "Strippenkaart", en: "Class pack" },
    title: { nl: "5 sessies", en: "5 sessions" },
    bullets: {
      nl: ["5 groepslessen", "Geldig 3 maanden", "Alle lestypes", "Geen abonnement"],
      en: ["5 group classes", "Valid 3 months", "All class types", "No subscription"],
    },
    note: { nl: "Wennen aan het ritme zonder verplichting", en: "Ease into the rhythm without commitment" },
    cta: { nl: "Koop strippenkaart", en: "Get pass" },
    popular: false,
  },
  {
    key: "joh_pack_10",
    price: "€110",
    period: { nl: "10 lessen · 6 mnd geldig", en: "10 classes · valid 6 months" },
    label: { nl: "Strippenkaart", en: "Class pack" },
    title: { nl: "10 sessies", en: "10 sessions" },
    bullets: {
      nl: ["10 groepslessen", "Geldig 6 maanden", "Elke les op het rooster", "Geen abonnement"],
      en: ["10 group classes", "Valid 6 months", "Any class on the schedule", "No subscription"],
    },
    note: { nl: "Beste waarde per les — flexibel te gebruiken", en: "Best value per class — use flexibly" },
    cta: { nl: "Koop strippenkaart", en: "Get pass" },
    popular: false,
  },
  {
    key: "joh_pt",
    price: { nl: "Op maat", en: "Tailored" },
    period: { nl: "1:1 sessie", en: "1:1 session" },
    label: { nl: "Personal training", en: "Personal training" },
    title: { nl: "Personal Training", en: "Personal Training" },
    bullets: {
      nl: ["1:1 begeleiding", "Programma op jouw doel", "Techniek, conditie of wedstrijd-prep", "Flexibele planning"],
      en: ["1:1 coaching", "Programme tailored to your goal", "Technique, conditioning or fight prep", "Flexible planning"],
    },
    note: { nl: "Volledige aandacht van de coach", en: "Full attention from the coach" },
    cta: { nl: "Vraag aan", en: "Enquire" },
    popular: false,
    isPT: true,
  },
];

function MembershipItem({ item, open, onToggle, lang }) {
  const price = typeof item.price === "object" ? item.price[lang] : item.price;
  const isCurrency = typeof item.price === "string" && item.price.startsWith("€");
  return (
    <div className={`relative border transition-colors ${open ? "bg-[#F5F3F0]" : "bg-[#EAE7E2]"} ${item.popular ? "border-[color:var(--joh-accent)] border-2" : "border-[#0A0A0A]"}`} data-testid={`membership-item-${item.key}`}>
      {item.popular && (
        <div className="absolute -top-3 left-6 md:left-8 chip px-3 py-1 uppercase" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="membership-popular-badge">
          {lang === "en" ? "Most popular" : "Meest populair"}
        </div>
      )}
      <button
        onClick={onToggle}
        className="w-full text-left px-6 md:px-8 py-6 md:py-7 flex items-center justify-between gap-6 group"
        data-testid={`membership-toggle-${item.key}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-6 md:gap-10 flex-1 min-w-0">
          <span className="font-display uppercase text-2xl md:text-4xl text-[#0A0A0A] truncate">{item.title[lang]}</span>
        </div>
        <div className="flex items-center gap-4 md:gap-8 shrink-0">
          <div className="text-right">
            <div className={`font-display leading-none text-[#0A0A0A] ${isCurrency ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl font-serif-italic"}`}>{price}</div>
            <div className="chip text-[#4A4A4A] mt-1">{item.period[lang]}</div>
          </div>
          <Plus size={20} className={`text-[#0A0A0A] transition-transform duration-500 ${open ? "rotate-45" : ""}`}/>
        </div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <div className="px-6 md:px-8 pb-8 md:pb-10 border-t border-[#0A0A0A]/25 pt-6">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {item.bullets[lang].map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[#0A0A0A]"><Check size={16} strokeWidth={1.5} className="mt-1 shrink-0"/> <span>{b}</span></li>
            ))}
          </ul>
          <div className="mt-6 chip text-[#4A4A4A]">{item.note[lang]}</div>
          {item.isPT ? (
            <a href="#pt" className="btn mt-6 inline-flex" data-testid={`membership-select-${item.key}`}>
              {item.cta[lang]} <ArrowUpRight size={14}/>
            </a>
          ) : (
            <Link to={`/join?plan=${item.key}`} className="btn mt-6 inline-flex" data-testid={`membership-select-${item.key}`}>
              {item.cta[lang]} <ArrowUpRight size={14}/>
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function IntakeSection({ n, title, activeStep, children }) {
  if (activeStep !== Number(n)) return null;
  return (
    <div className="mt-6 first:mt-0" data-testid={`intake-section-${n}`}>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-display text-3xl accent-ink" style={{ color: "var(--joh-accent)" }}>{n}</span>
        <h4 className="font-display uppercase text-lg md:text-xl text-[#0A0A0A]">{title}</h4>
      </div>
      {children}
    </div>
  );
}


const INTAKE_DRAFT_KEY = "joh_intake_draft";
const INITIAL_INTAKE = {
  first_name: "", last_name: "", date_of_birth: "", email: "", phone: "",
  street: "", house_number: "", postal_code: "", city: "",
  emergency_name: "", emergency_relation: "", emergency_phone: "",
  boxing_experience: "no",
  goals: [],
  goals_other: "",
  has_medical: "no",
  medical_details: "",
  notes: "",
  accept_terms: false,
  accept_privacy: false,
  accept_health_truth: false,
  consent_media: false,
  consent_marketing: false,
};

function loadIntakeDraft(lookupKey) {
  try {
    const raw = localStorage.getItem(INTAKE_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.lookupKey !== lookupKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function MembershipIntakeForm({ lookupKey, lang, planLabel, price, promoCode, promoNote }) {
  const draft = loadIntakeDraft(lookupKey);
  const [step, setStep] = useState(draft?.step || 1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_INTAKE, ...(draft?.form || {}) });
  const [restored, setRestored] = useState(!!draft);
  const [phoneErr, setPhoneErr] = useState("");
  const skipSaveRef = useRef(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleGoal = (g) => setForm(f => ({ ...f, goals: f.goals.includes(g) ? f.goals.filter(x => x !== g) : [...f.goals, g] }));

  // Autosave draft to localStorage on every change (skipped after successful clear)
  useEffect(() => {
    if (skipSaveRef.current) return;
    // Don't save an empty pristine draft
    const anyFilled = Object.entries(form).some(([k, v]) => {
      if (INITIAL_INTAKE[k] === v) return false;
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== false;
    });
    if (!anyFilled && step === 1) return;
    try {
      localStorage.setItem(INTAKE_DRAFT_KEY, JSON.stringify({
        lookupKey, step, form, savedAt: Date.now(),
      }));
    } catch {}
  }, [form, step, lookupKey]);

  const clearDraft = () => {
    skipSaveRef.current = true;
    try { localStorage.removeItem(INTAKE_DRAFT_KEY); } catch {}
    setForm(INITIAL_INTAKE);
    setStep(1);
    setRestored(false);
    // Re-enable save after next render
    setTimeout(() => { skipSaveRef.current = false; }, 0);
  };

  const GOAL_OPTIONS = [
    { key: "boksen", nl: "Boksen leren", en: "Learn to box" },
    { key: "conditie", nl: "Conditie verbeteren", en: "Improve conditioning" },
    { key: "sterker", nl: "Sterker en fitter worden", en: "Get stronger & fitter" },
    { key: "zelfvertrouwen", nl: "Zelfvertrouwen ontwikkelen", en: "Build confidence" },
    { key: "wedstrijden", nl: "Voorbereiden op wedstrijden", en: "Prepare for fights" },
    { key: "anders", nl: "Anders", en: "Other" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone, { required: true });
    if (pErr) { setPhoneErr(pErr); toast.error(pErr); return; }
    if (!form.accept_terms || !form.accept_privacy || !form.accept_health_truth) {
      toast.error(lang === "en" ? "Please accept all mandatory conditions" : "Accepteer alle verplichte voorwaarden");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/forms/membership-signup", {
        ...form,
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        promo_code: promoCode || undefined,
      });
      // Clear draft — signup was successful
      skipSaveRef.current = true;
      try { localStorage.removeItem(INTAKE_DRAFT_KEY); } catch {}
      toast.success(lang === "en" ? "Redirecting to secure checkout…" : "Doorsturen naar veilige checkout…");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Signup failed" : "Aanmelden mislukt"));
      setSubmitting(false);
    }
  };

  const inputCls = "editorial-input";
  const labelCls = "chip text-[#4A4A4A]";
  const TOTAL_STEPS = 6;
  const isStepValid = (n) => {
    if (n === 1) return !!(form.first_name && form.last_name && form.date_of_birth && form.email && form.phone);
    if (n === 2) return !!(form.street && form.house_number && form.postal_code && form.city);
    if (n === 3) return !!(form.emergency_name && form.emergency_relation && form.emergency_phone);
    if (n === 4) return !!(form.goals.length && (form.has_medical === "no" || form.medical_details));
    if (n === 5) return true; // optional
    if (n === 6) return form.accept_terms && form.accept_privacy && form.accept_health_truth;
    return true;
  };
  const next = () => {
    if (!isStepValid(step)) {
      toast.error(lang === "en" ? "Please complete this step" : "Vul deze stap eerst in");
      return;
    }
    setStep(s => Math.min(TOTAL_STEPS, s + 1));
    setTimeout(() => document.getElementById(`intake-form-${lookupKey}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  return (
    <form onSubmit={submit} id={`intake-form-${lookupKey}`} className="mt-8 border-t border-[#0A0A0A]/25 pt-8" data-testid={`membership-intake-${lookupKey}`}>
      {promoCode && (
        <div className="mb-6 inline-flex items-center gap-3 border px-4 py-2" style={{ borderColor: "var(--joh-accent)" }} data-testid="intake-promo-active">
          <span className="chip" style={{ color: "var(--joh-accent)" }}>{promoCode}</span>
          <span className="text-sm text-[#0A0A0A]">{promoNote || (lang === "en" ? "Promo active" : "Actie actief")}</span>
        </div>
      )}
      {/* Progress bar */}
      {(() => {
        const sections = [
          { n: 1, label: lang === "en" ? "Personal" : "Persoonlijk" },
          { n: 2, label: lang === "en" ? "Address" : "Adres" },
          { n: 3, label: lang === "en" ? "Emergency" : "Noodcontact" },
          { n: 4, label: lang === "en" ? "Health" : "Gezondheid" },
          { n: 5, label: lang === "en" ? "Notes" : "Opmerkingen" },
          { n: 6, label: lang === "en" ? "Consent" : "Akkoord" },
        ];
        const pct = Math.round((step / TOTAL_STEPS) * 100);
        return (
          <div className="sticky top-4 z-10 bg-[#F5F3F0]/90 backdrop-blur-sm border border-[#0A0A0A] p-4 mb-8" data-testid={`intake-progress-${lookupKey}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div className="chip text-[#4A4A4A]">{lang === "en" ? "Step" : "Stap"} · {sections[step - 1]?.label}</div>
              <div className="chip text-[#0A0A0A]" data-testid={`intake-progress-count-${lookupKey}`}>
                <span className="font-display text-lg">{step}</span>/{TOTAL_STEPS}
              </div>
            </div>
            <div className="h-1.5 bg-[#0A0A0A]/10 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 transition-[width] duration-500" style={{ width: `${pct}%`, background: "var(--joh-accent)" }}/>
            </div>
            <div className="mt-3 grid grid-cols-6 gap-1 md:gap-2">
              {sections.map(s => {
                const done = s.n < step;
                const current = s.n === step;
                return (
                  <button
                    type="button"
                    key={s.n}
                    onClick={() => (s.n < step ? setStep(s.n) : null)}
                    disabled={s.n > step}
                    className="text-center group disabled:cursor-not-allowed"
                    data-testid={`intake-progress-step-${s.n}`}
                  >
                    <div
                      className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center text-xs font-mono ${done ? "text-[#0A0A0A]" : current ? "text-[#0A0A0A]" : "text-[#4A4A4A]"}`}
                      style={done ? { background: "var(--joh-accent)" } : current ? { background: "var(--joh-accent)", boxShadow: "0 0 0 2px #0A0A0A" } : { border: "1px solid rgba(10,10,10,0.3)", background: "transparent" }}
                    >
                      {done ? "✓" : s.n}
                    </div>
                    <div className={`chip mt-1 hidden md:block ${done || current ? "text-[#0A0A0A]" : "text-[#4A4A4A]"}`}>{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
      <div className="mb-6 flex items-baseline justify-between gap-4">
        <div className="chip text-[#4A4A4A]">{lang === "en" ? "Complete your membership" : "Rond je aanmelding af"}</div>
        <div className="chip text-[#0A0A0A]">{planLabel} · <span className="font-display text-lg">{price}</span></div>
      </div>

      {restored && (
        <div className="mb-6 border-2 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" style={{ borderColor: "var(--joh-accent)", background: "rgba(245, 168, 43, 0.08)" }} data-testid={`intake-restored-${lookupKey}`}>
          <div className="text-sm text-[#0A0A0A]">
            <span className="font-display text-lg mr-2" style={{ color: "var(--joh-accent)" }}>↺</span>
            {lang === "en"
              ? `We restored your progress from where you left off (step ${step}/${TOTAL_STEPS}).`
              : `We hebben je voortgang hersteld waar je gebleven was (stap ${step}/${TOTAL_STEPS}).`}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRestored(false)} className="chip text-[#0A0A0A] border border-[#0A0A0A] px-3 py-1 hover:bg-[#0A0A0A] hover:text-[#F5F3F0]" data-testid={`intake-restored-continue-${lookupKey}`}>
              {lang === "en" ? "Continue" : "Verder"}
            </button>
            <button type="button" onClick={clearDraft} className="chip text-[#4A4A4A] hover:text-[#0A0A0A] px-3 py-1" data-testid={`intake-restored-clear-${lookupKey}`}>
              {lang === "en" ? "Start fresh" : "Begin opnieuw"}
            </button>
          </div>
        </div>
      )}

      <IntakeSection activeStep={step} n="1" title={lang === "en" ? "Personal details" : "Persoonlijke gegevens"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>{lang === "en" ? "First name*" : "Voornaam*"}</label><input required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="intake-first-name"/></div>
          <div><label className={labelCls}>{lang === "en" ? "Last name*" : "Achternaam*"}</label><input required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="intake-last-name"/></div>
          <div>
            <label className={labelCls}>{lang === "en" ? "Date of birth*" : "Geboortedatum*"}</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={`${inputCls} flex items-center justify-between gap-2 text-left ${form.date_of_birth ? "" : "text-[#4A4A4A]"}`} data-testid="intake-dob">
                  <span>{form.date_of_birth
                    ? new Date(form.date_of_birth + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { day: "numeric", month: "long", year: "numeric" })
                    : (lang === "en" ? "Select date" : "Kies een datum")}</span>
                  <CalendarDays size={16} className="shrink-0 opacity-60"/>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border border-[#0A0A0A] bg-[#F5F3F0] rounded-none shadow-none" align="start">
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  showOutsideDays={false}
                  fromYear={1940}
                  toYear={new Date().getFullYear() - 14}
                  defaultMonth={form.date_of_birth ? new Date(form.date_of_birth + "T00:00:00") : new Date(2000, 0, 1)}
                  selected={form.date_of_birth ? new Date(form.date_of_birth + "T00:00:00") : undefined}
                  className="p-2"
                  classNames={{
                    caption_label: "hidden",
                    caption_dropdowns: "flex gap-2 items-center",
                    dropdown: "bg-transparent text-sm font-medium outline-none cursor-pointer",
                    head_cell: "text-[#4A4A4A] w-7 font-normal text-[0.7rem]",
                    day: "h-7 w-7 p-0 text-xs font-normal aria-selected:opacity-100 hover:bg-[#0A0A0A] hover:text-[#F5F3F0] transition-colors",
                    cell: "p-0 text-center text-xs",
                    row: "flex w-full mt-1",
                    month: "space-y-2",
                    nav_button: "h-6 w-6 bg-transparent p-0 opacity-40 hover:opacity-100 border border-[#0A0A0A]/20",
                  }}
                  onSelect={(d) => {
                    if (!d) return;
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    setForm(f => ({ ...f, date_of_birth: iso }));
                  }}
                  data-testid="intake-dob-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div><label className={labelCls}>{lang === "en" ? "Email*" : "E-mail*"}</label><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="intake-email"/></div>
          <div className="md:col-span-2"><PhoneField dark value={form.phone} onChange={(v) => { setForm(f => ({ ...f, phone: v })); setPhoneErr(validatePhone(v, { required: true })); }} error={phoneErr} testid="intake-phone" label={lang === "en" ? "Phone*" : "Telefoonnummer*"}/></div>
        </div>
      </IntakeSection>

      <IntakeSection activeStep={step} n="2" title={lang === "en" ? "Address" : "Adresgegevens"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2"><label className={labelCls}>{lang === "en" ? "Street*" : "Straatnaam*"}</label><input required value={form.street} onChange={set("street")} className={inputCls} data-testid="intake-street"/></div>
          <div><label className={labelCls}>{lang === "en" ? "House number*" : "Huisnummer + toevoeging*"}</label><input required value={form.house_number} onChange={set("house_number")} className={inputCls} data-testid="intake-house"/></div>
          <div><label className={labelCls}>{lang === "en" ? "Postal code*" : "Postcode*"}</label><input required value={form.postal_code} onChange={set("postal_code")} className={inputCls} data-testid="intake-postal"/></div>
          <div className="md:col-span-2"><label className={labelCls}>{lang === "en" ? "City*" : "Woonplaats*"}</label><input required value={form.city} onChange={set("city")} className={inputCls} data-testid="intake-city"/></div>
        </div>
      </IntakeSection>

      <IntakeSection activeStep={step} n="3" title={lang === "en" ? "Emergency contact" : "Noodcontact"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className={labelCls}>{lang === "en" ? "Contact name*" : "Naam contactpersoon*"}</label><input required value={form.emergency_name} onChange={set("emergency_name")} className={inputCls} data-testid="intake-emerg-name"/></div>
          <div><label className={labelCls}>{lang === "en" ? "Relation*" : "Relatie tot deze persoon*"}</label><input required value={form.emergency_relation} onChange={set("emergency_relation")} className={inputCls} placeholder={lang === "en" ? "e.g. partner, parent" : "bv. partner, ouder"} data-testid="intake-emerg-relation"/></div>
          <div className="md:col-span-2"><label className={labelCls}>{lang === "en" ? "Phone*" : "Telefoonnummer contactpersoon*"}</label><input required value={form.emergency_phone} onChange={set("emergency_phone")} className={inputCls} data-testid="intake-emerg-phone"/></div>
        </div>
      </IntakeSection>

      <IntakeSection activeStep={step} n="4" title={lang === "en" ? "Training & health" : "Training en gezondheid"}>
        <div>
          <label className={labelCls}>{lang === "en" ? "Prior boxing / combat sport experience?*" : "Boks- of vechtsportervaring?*"}</label>
          <div className="mt-2 flex gap-6">
            {[["yes", lang === "en" ? "Yes" : "Ja"], ["no", lang === "en" ? "No" : "Nee"]].map(([v,l]) => (
              <label key={v} className="flex items-center gap-2 text-[#0A0A0A]">
                <input type="radio" name={`boxexp-${lookupKey}`} value={v} checked={form.boxing_experience === v} onChange={set("boxing_experience")} data-testid={`intake-boxexp-${v}`}/>
                <span>{l}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <label className={labelCls}>{lang === "en" ? "What are your goals?* (multiple)" : "Wat wil je bereiken?* (meerdere)"}</label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {GOAL_OPTIONS.map(g => (
              <label key={g.key} className="flex items-center gap-2 text-[#0A0A0A]" data-testid={`intake-goal-${g.key}`}>
                <input type="checkbox" checked={form.goals.includes(g.key)} onChange={() => toggleGoal(g.key)}/>
                <span>{lang === "en" ? g.en : g.nl}</span>
              </label>
            ))}
          </div>
          {form.goals.includes("anders") && (
            <input value={form.goals_other} onChange={set("goals_other")} className={`${inputCls} mt-3`} placeholder={lang === "en" ? "Please specify" : "Namelijk..."} data-testid="intake-goals-other"/>
          )}
        </div>

        <div className="mt-6">
          <label className={labelCls}>{lang === "en" ? "Any injuries / medical conditions?*" : "Blessures / medische omstandigheden?*"}</label>
          <div className="mt-2 flex gap-6">
            {[["no", lang === "en" ? "No" : "Nee"], ["yes", lang === "en" ? "Yes, namely..." : "Ja, namelijk..."]].map(([v,l]) => (
              <label key={v} className="flex items-center gap-2 text-[#0A0A0A]">
                <input type="radio" name={`med-${lookupKey}`} value={v} checked={form.has_medical === v} onChange={set("has_medical")} data-testid={`intake-med-${v}`}/>
                <span>{l}</span>
              </label>
            ))}
          </div>
          {form.has_medical === "yes" && (
            <textarea value={form.medical_details} onChange={set("medical_details")} rows={3} className={`${inputCls} mt-3 resize-none`} placeholder={lang === "en" ? "Please describe" : "Beschrijf hier..."} data-testid="intake-medical-details"/>
          )}
        </div>
      </IntakeSection>

      <IntakeSection activeStep={step} n="5" title={lang === "en" ? "Additional info" : "Aanvullende informatie"}>
        <label className={labelCls}>{lang === "en" ? "Questions or remarks?" : "Vragen of opmerkingen?"}</label>
        <textarea value={form.notes} onChange={set("notes")} rows={3} className={`${inputCls} resize-none`} data-testid="intake-notes"/>
      </IntakeSection>

      <IntakeSection activeStep={step} n="6" title={lang === "en" ? "Consent" : "Akkoord en toestemming"}>
        <div className="space-y-3 text-[#0A0A0A]">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="intake-accept-terms">
            <input required type="checkbox" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-1"/>
            <span className="text-sm">
              {lang === "en" ? "I have read and agree to the " : "Ik heb de "}
              <a href="/voorwaarden" target="_blank" rel="noreferrer" className="accent-ink underline underline-offset-2" style={{color: "var(--joh-accent)"}}>
                {lang === "en" ? "terms & conditions" : "algemene voorwaarden"}
              </a>
              {lang === "en" ? ".*" : " gelezen en ga hiermee akkoord.*"}
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="intake-accept-privacy">
            <input required type="checkbox" checked={form.accept_privacy} onChange={set("accept_privacy")} className="mt-1"/>
            <span className="text-sm">
              {lang === "en" ? "I have read the " : "Ik heb de "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="accent-ink underline underline-offset-2" style={{color: "var(--joh-accent)"}}>
                {lang === "en" ? "privacy statement" : "privacyverklaring"}
              </a>
              {lang === "en" ? " and understand how my personal data is processed.*" : " gelezen en begrijp hoe mijn persoonsgegevens worden verwerkt.*"}
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="intake-accept-health">
            <input required type="checkbox" checked={form.accept_health_truth} onChange={set("accept_health_truth")} className="mt-1"/>
            <span className="text-sm">
              {lang === "en"
                ? "I confirm that I answered the health questions truthfully and will notify JOH. of any relevant changes.*"
                : "Ik verklaar dat ik de gezondheidsvragen naar waarheid heb ingevuld en wijzigingen doorgeef die belangrijk zijn voor mijn deelname aan JOH. Boxing.*"}
            </span>
          </label>
          <div className="pt-3 border-t border-[#0A0A0A]/15 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="intake-consent-media">
              <input type="checkbox" checked={form.consent_media} onChange={set("consent_media")} className="mt-1"/>
              <span className="text-sm text-[#4A4A4A]">
                {lang === "en"
                  ? "I give permission to use recognisable photos/videos of me for JOH.'s website and social media."
                  : "Ik geef toestemming om foto's en video's waarop ik herkenbaar ben te gebruiken voor de website en socialmediakanalen van JOH. Boxing."}
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="intake-consent-marketing">
              <input type="checkbox" checked={form.consent_marketing} onChange={set("consent_marketing")} className="mt-1"/>
              <span className="text-sm text-[#4A4A4A]">
                {lang === "en"
                  ? "I want to receive news, updates and offers from JOH. by email or WhatsApp."
                  : "Ik wil nieuws, updates en aanbiedingen van JOH. Boxing ontvangen via e-mail of WhatsApp."}
              </span>
            </label>
          </div>
        </div>
      </IntakeSection>

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={step === 1}
          className={`btn-ghost ${step === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid={`intake-back-${lookupKey}`}
        >
          ← {lang === "en" ? "Back" : "Vorige"}
        </button>
        {step < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={next}
            className="btn"
            data-testid={`intake-next-${lookupKey}`}
          >
            {lang === "en" ? "Next" : "Volgende"} <ArrowUpRight size={14}/>
          </button>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            className="btn"
            data-testid={`intake-submit-${lookupKey}`}
          >
            {submitting ? (lang === "en" ? "Sending…" : "Versturen…") : (<>{lang === "en" ? "Confirm & pay securely" : "Bevestig & betaal veilig"} <ArrowUpRight size={14}/></>)}
          </button>
        )}
      </div>
      <p className="chip text-[#4A4A4A] mt-3 text-center">
        {step < TOTAL_STEPS
          ? (lang === "en" ? `Step ${step} of ${TOTAL_STEPS}` : `Stap ${step} van ${TOTAL_STEPS}`)
          : (lang === "en" ? "Secure payment via Stripe. Cancel monthly." : "Veilige betaling via Stripe. Maandelijks opzegbaar.")}
      </p>
    </form>
  );
}

function Membership() {
  const { t, lang } = useT();
  const [open, setOpen] = useState("joh_membership_2x");
  return (
    <section id="membership" className="py-32 md:py-48 px-6 md:px-10" data-testid="membership-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="section-tag">{t("membership.ch")}</div>
          <h2 className="font-display uppercase text-[13vw] lg:text-[6.5vw] text-[#0A0A0A] leading-[0.86] mt-6" data-testid="membership-title">
            <LineRise><Editable k="membership.h1.1">Become</Editable></LineRise>
            <LineRise delay={0.1}><span className="font-serif-italic normal-case text-[#4A4A4A] tracking-tight"><Editable k="membership.h1.2">part</Editable></span></LineRise>
            <LineRise delay={0.2}><Editable k="membership.h1.3">of JOH.</Editable></LineRise>
          </h2>
          <Rise delay={0.15}>
            <p className="mt-8 text-[#0A0A0A] text-base md:text-lg leading-relaxed max-w-md">{t("membership.blurb")}</p>
            <Link to="/join" className="btn mt-8" data-testid="membership-view-all-btn">
              {lang === "en" ? "See all options" : "Bekijk alle opties"} <ArrowUpRight size={14}/>
            </Link>
          </Rise>
        </div>

        <Rise delay={0.2} className="lg:col-span-7">
          <div className="flex flex-col gap-4" data-testid="membership-accordion">
            {MEMBERSHIP_ITEMS.slice(0, 2).map((item) => (
              <MembershipItem
                key={item.key}
                item={item}
                lang={lang}
                open={open === item.key}
                onToggle={() => setOpen(open === item.key ? null : item.key)}
              />
            ))}
            <Link to="/join" className="btn-ghost justify-center mt-2" data-testid="membership-see-more">
              {lang === "en" ? "See all plans (5 options)" : "Bekijk alle opties (5 plannen)"} <ArrowUpRight size={14}/>
            </Link>
          </div>
        </Rise>
      </div>
    </section>
  );
}

/* --------------------------------- ABOUT --------------------------------- */
function About() {
  const { t } = useT();
  const media = useMedia();
  return (
    <section id="about" className="bg-[#EAE7E2] py-32 md:py-48 line-t line-b" data-testid="about-section">
      <div className="px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2 section-tag">{t("about.ch")}</div>
        <div className="lg:col-span-6">
          <h2 className="font-display uppercase text-[13vw] lg:text-[7.5vw] text-[#0A0A0A] leading-[0.86]" data-testid="about-title">
            <LineRise><Editable k="about.h1.1">Built from</Editable></LineRise>
            <LineRise delay={0.1}><span className="font-serif-italic normal-case text-[#4A4A4A] tracking-tight"><Editable k="about.h1.2">experience.</Editable></span></LineRise>
            <LineRise delay={0.2}><Editable k="about.h1.3">Created for</Editable></LineRise>
            <LineRise delay={0.3}><Editable k="about.h1.4">community.</Editable></LineRise>
          </h2>
          <Rise delay={0.2}>
            <div className="mt-10 max-w-xl space-y-5 text-[#0A0A0A] text-base md:text-lg leading-relaxed">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
            </div>
            <Link to="/join" className="btn mt-10 inline-flex" data-testid="about-train-btn">
              {t("about.btn")} <ArrowUpRight size={14} />
            </Link>
          </Rise>
        </div>
        <Rise delay={0.25} className="lg:col-span-4">
          <div className="relative aspect-[3/4] overflow-hidden border-2" style={{borderColor: "var(--joh-accent)"}}>
            <img src={media("home.about.coach")} alt="Coach Glenn" className="w-full h-full object-cover" style={{ filter: "grayscale(10%) contrast(1.05)" }} />
            <div className="absolute bottom-4 left-4 chip text-[#F5F3F0]">Glenn Mulala / Head coach</div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-[#0A0A0A]">
            <div>
              <div className="font-display text-4xl">10+</div>
              <div className="chip text-[#4A4A4A]">{t("about.stat1")}</div>
            </div>
            <div>
              <div className="font-display text-4xl">01</div>
              <div className="chip text-[#4A4A4A]">{t("about.stat2")}</div>
            </div>
            <div>
              <div className="font-display text-4xl">Top 5</div>
              <div className="chip text-[#4A4A4A]">{t("about.stat3")}</div>
            </div>
          </div>
        </Rise>
      </div>
    </section>
  );
}

/* --------------------------------- VISION --------------------------------- */
function Vision() {
  const { t } = useT();
  return (
    <section className="py-32 md:py-48 px-6 md:px-10" data-testid="vision-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-2 section-tag">{t("vision.ch")}</div>
        <h2 className="lg:col-span-10 font-display uppercase text-[13vw] lg:text-[9vw] text-[#0A0A0A] leading-[0.86]" data-testid="vision-title">
          <LineRise><Editable k="vision.h1.1">Master the</Editable></LineRise>
          <LineRise delay={0.1}><span className="font-serif-italic normal-case text-[#4A4A4A] tracking-tight"><Editable k="vision.h1.2">fundamentals.</Editable></span></LineRise>
        </h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Rise className="lg:col-span-4">
          <p className="text-[#0A0A0A] text-base md:text-lg leading-relaxed max-w-md">{t("vision.blurb")}</p>
        </Rise>
        <div className="lg:col-span-8">
          <div className="line-t">
            {VISION_KEYS.map((v, i) => (
              <Rise key={v.n} delay={i * 0.08}>
                <div className="line-b py-6 md:py-8 grid grid-cols-12 gap-4 items-center group" data-testid={`vision-row-${v.n}`}>
                  <span className="col-span-2 md:col-span-1 chip text-[#4A4A4A]">{v.n}</span>
                  <span className="col-span-10 md:col-span-3 font-display uppercase text-2xl md:text-4xl text-[#0A0A0A]">{t(v.labelK)}</span>
                  <span className="col-span-12 md:col-span-8 text-[#4A4A4A] text-base md:text-lg">{t(v.textK)}</span>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- COMMUNITY --------------------------------- */
function Community() {
  const { t } = useT();
  const media = useMedia();
  const { scrollYProgress } = useScroll();
  // Alle impressie-foto's bewegen in hetzelfde tempo ("lopen gelijk")
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="community" className="bg-[#F5F3F0] py-32 md:py-48 px-6 md:px-10 relative overflow-hidden" data-testid="community-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-2 section-tag">{t("community.ch")}</div>
        <h2 className="lg:col-span-10 font-display uppercase text-[13vw] lg:text-[9vw] text-[#0A0A0A] leading-[0.86]" data-testid="community-title">
          <LineRise><Editable k="community.h1.1">We build</Editable></LineRise>
          <LineRise delay={0.1}><span className="font-serif-italic normal-case text-[#4A4A4A] tracking-tight"><Editable k="community.h1.2">each</Editable></span></LineRise>
          <LineRise delay={0.2}><Editable k="community.h1.3">other.</Editable></LineRise>
        </h2>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        <motion.div style={{ y: yImg }} className="col-span-12 md:col-span-4">
          <img src={media("home.community.1")} alt="" className="w-full aspect-[4/5] object-cover border-2" style={{ filter: "grayscale(15%) contrast(1.05)", borderColor: "var(--joh-accent)" }} />
        </motion.div>
        <motion.div style={{ y: yImg }} className="col-span-12 md:col-span-4">
          <img src={media("home.community.2")} alt="" className="w-full aspect-[4/5] object-cover border-2" style={{ filter: "grayscale(15%) contrast(1.05)", borderColor: "var(--joh-accent)" }} />
        </motion.div>
        <motion.div style={{ y: yImg }} className="col-span-12 md:col-span-4 relative">
          <img src={media("home.community.3")} alt="" className="w-full aspect-[4/5] object-cover border-2" style={{ filter: "grayscale(15%) contrast(1.05)", borderColor: "var(--joh-accent)" }} />
        </motion.div>
      </div>

      <Rise delay={0.2}>
        <div className="mt-16 max-w-2xl">
          <p className="text-[#0A0A0A] text-lg md:text-xl leading-relaxed mb-6">{t("community.blurb")}</p>
          <p className="text-[#0A0A0A] text-lg md:text-xl leading-relaxed">{t("community.invite")}</p>
          <Link to="/join" className="btn mt-6 inline-flex" data-testid="community-join-btn">
            {t("community.btn")} <ArrowUpRight size={14} />
          </Link>
        </div>
      </Rise>
    </section>
  );
}

/* --------------------------------- PT --------------------------------- */
function PersonalTraining() {
  const { t, lang } = useT();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", experience: "", goal: "", preferred_day: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone, { required: true });
    if (pErr) { setPhoneErr(pErr); toast.error(pErr); return; }
    setLoading(true);
    try {
      await api.post("/forms/personal-training", form);
      toast.success(t("toast.pt.ok"));
      setForm({ first_name: "", last_name: "", email: "", phone: "", experience: "", goal: "", preferred_day: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("toast.err"));
    }
    setLoading(false);
  };
  return (
    <section id="pt" className="py-32 md:py-48 line-t line-b bg-[#EAE7E2]" data-testid="pt-section">
      <div className="px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="section-tag">{t("pt.ch")}</div>
          <h2 className="font-display uppercase text-[13vw] lg:text-[7.5vw] text-[#0A0A0A] leading-[0.86] mt-6" data-testid="pt-title">
            <LineRise><Editable k="pt.h1.1">Your goal.</Editable></LineRise>
            <LineRise delay={0.1}><span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]"><Editable k="pt.h1.2">Your</Editable></span></LineRise>
            <LineRise delay={0.2}><Editable k="pt.h1.3">session.</Editable></LineRise>
          </h2>
          <Rise delay={0.15}>
            <p className="mt-8 text-[#0A0A0A] text-base md:text-lg leading-relaxed max-w-md">{t("pt.blurb")}</p>
            <ul className="mt-6 grid grid-cols-2 gap-3 max-w-md">
              {["pt.g1","pt.g2","pt.g3","pt.g4","pt.g5","pt.g6","pt.g7"].map((k,i)=>(
                <li key={i} className="flex items-center gap-2 text-[#0A0A0A] text-sm"><Plus size={14} strokeWidth={1.5}/>{t(k)}</li>
              ))}
            </ul>
          </Rise>
        </div>

        <Rise delay={0.2} className="lg:col-span-6">
          <form onSubmit={submit} className="border border-[#0A0A0A] bg-[#F5F3F0] p-6 md:p-10" data-testid="pt-form">
            <div className="chip text-[#4A4A4A] mb-6">{t("pt.form.title")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "First name*" : "Voornaam*"}</label>
                <input required value={form.first_name} onChange={set("first_name")} className="editorial-input" data-testid="pt-first-name" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Last name*" : "Achternaam*"}</label>
                <input required value={form.last_name} onChange={set("last_name")} className="editorial-input" data-testid="pt-last-name" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("form.email")}</label>
                <input required type="email" value={form.email} onChange={set("email")} className="editorial-input" placeholder="jij@voorbeeld.com" data-testid="pt-email" />
              </div>
              <div>
                <PhoneField value={form.phone} onChange={(v) => { setForm(f => ({ ...f, phone: v })); setPhoneErr(validatePhone(v, { required: true })); }} error={phoneErr} testid="pt-phone" label={t("form.phone")} />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("form.exp")}</label>
                <input value={form.experience} onChange={set("experience")} className="editorial-input" data-testid="pt-experience" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("form.goal")}</label>
                <input value={form.goal} onChange={set("goal")} className="editorial-input" data-testid="pt-goal" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("form.day")}</label>
                <input value={form.preferred_day} onChange={set("preferred_day")} className="editorial-input" data-testid="pt-day" />
              </div>
              <div className="md:col-span-2">
                <label className="chip text-[#4A4A4A]">{t("form.msg")}</label>
                <input value={form.message} onChange={set("message")} className="editorial-input" data-testid="pt-message" />
              </div>
            </div>
            <button disabled={loading} type="submit" className="btn mt-8 w-full justify-center" data-testid="pt-submit-btn">
              {loading ? t("loading.send") : t("pt.form.submit")} <ArrowUpRight size={14}/>
            </button>
          </form>
        </Rise>
      </div>
    </section>
  );
}

/* --------------------------------- CONTACT --------------------------------- */
function Contact() {
  const { t, lang } = useT();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "Proefles", message: "" });
  const [loading, setLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone);
    if (pErr) { setPhoneErr(pErr); toast.error(pErr); return; }
    setLoading(true);
    try {
      await api.post("/forms/contact", form);
      toast.success(t("toast.contact.ok"));
      setForm({ first_name: "", last_name: "", email: "", phone: "", subject: "Proefles", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("toast.err"));
    }
    setLoading(false);
  };
  return (
    <section id="contact" className="py-32 md:py-48 px-6 md:px-10" data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="section-tag">{t("contact.ch")}</div>
          <h2 className="font-display uppercase text-[13vw] lg:text-[7.5vw] leading-[0.86] mt-6 text-[#0A0A0A]" data-testid="contact-title">
            <LineRise><Editable k="contact.h1.1">Step</Editable></LineRise>
            <LineRise delay={0.1}><Editable k="contact.h1.2">into</Editable></LineRise>
            <LineRise delay={0.2}><span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]"><Editable k="contact.h1.3">the</Editable></span></LineRise>
            <LineRise delay={0.3}><Editable k="contact.h1.4">gym.</Editable></LineRise>
          </h2>
          <Rise delay={0.15}>
            <div className="mt-10 space-y-4 text-[#0A0A0A]">
              <div className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.5} className="mt-1"/>
                <div>
                  <div className="font-display uppercase text-2xl">JOH. Boxing</div>
                  <div className="text-[#4A4A4A]"><a href="https://maps.google.com/?q=Rhoneweg+38+1043+AH+Amsterdam" target="_blank" rel="noreferrer" className="accent-hover" data-testid="contact-address-link">Rhoneweg 38, 1043 AH</a><br/><span className="chip">Amsterdam</span></div>
                </div>
              </div>
              <a href="mailto:info@johboxing.com" className="flex items-center gap-4 accent-hover" data-testid="contact-email-link"><Mail size={18} strokeWidth={1.5}/> info@johboxing.com</a>
              <a href="https://wa.me/31626923741" target="_blank" rel="noreferrer" className="flex items-center gap-4 accent-hover" data-testid="contact-whatsapp-link"><MessageCircle size={18} strokeWidth={1.5}/> WhatsApp · 06 26923741</a>
              <a href="https://instagram.com/johboxing" target="_blank" rel="noreferrer" className="flex items-center gap-4 accent-hover" data-testid="contact-instagram-link"><Instagram size={18} strokeWidth={1.5}/> @johboxing</a>
            </div>
          </Rise>
        </div>

        <Rise delay={0.2} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-[#0A0A0A] p-6 md:p-10" data-testid="contact-form">
            <div className="chip text-[#4A4A4A] mb-6">{t("contact.form.title")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "First name*" : "Voornaam*"}</label>
                <input required value={form.first_name} onChange={set("first_name")} className="editorial-input" data-testid="contact-first-name" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Last name*" : "Achternaam*"}</label>
                <input required value={form.last_name} onChange={set("last_name")} className="editorial-input" data-testid="contact-last-name" />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("form.email")}</label>
                <input required type="email" value={form.email} onChange={set("email")} className="editorial-input" data-testid="contact-email" />
              </div>
              <div>
                <PhoneField value={form.phone} onChange={(v) => { setForm(f => ({ ...f, phone: v })); setPhoneErr(validatePhone(v)); }} error={phoneErr} testid="contact-phone" label={lang === "en" ? "Phone (optional)" : "Telefoon (optioneel)"} />
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{t("contact.subject")}</label>
                <select value={form.subject} onChange={set("subject")} className="editorial-input" data-testid="contact-subject">
                  <option>{t("subj.trial")}</option>
                  <option>{t("subj.membership")}</option>
                  <option>{t("subj.pt")}</option>
                  <option>{t("subj.collab")}</option>
                  <option>{t("subj.youth")}</option>
                  <option>{t("subj.other")}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="chip text-[#4A4A4A]">{t("form.msg")}</label>
                <textarea required rows={3} value={form.message} onChange={set("message")} className="editorial-input resize-none" data-testid="contact-message" />
              </div>
            </div>
            <button disabled={loading} type="submit" className="btn mt-8" data-testid="contact-submit-btn">
              {loading ? t("loading.send") : t("contact.submit")} <ArrowUpRight size={14}/>
            </button>
          </form>
        </Rise>
      </div>
    </section>
  );
}

/* --------------------------------- PAGE --------------------------------- */
/* --------------------- BEWERKBARE BANNERS (via admin) --------------------- */
function SiteBanners() {
  const { lang } = useT();
  const { content } = useSiteContent();
  const promo = content?.promo_banner;
  const promoText = promo?.active ? (lang === "en" ? promo.text_en : promo.text_nl) : null;
  if (!promoText) return null;
  return (
    <div className="relative z-40" data-testid="site-banners">
      <div className="bg-[#0A0A0A] text-[#F5F3F0] px-6 md:px-10 py-2.5 flex flex-col md:flex-row md:items-center gap-1 md:gap-4 border-b border-[#D4A93A]/40" data-testid="promo-banner">
        <span className="chip" style={{ color: "var(--joh-accent)" }}>{lang === "en" ? "Offer" : "Actie"}</span>
        <span className="text-sm">{promoText}</span>
        {promo?.code && <span className="chip md:ml-auto border border-[#D4A93A]/60 px-2 py-0.5" style={{ color: "var(--joh-accent)" }}>{promo.code}</span>}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <main className="grain">
      <SiteBanners />
      <Hero />
      <EditorialMarquee />
      <ClassesRSVP />
      <ClassesTeaser />
      <Intro />
      <Values />
      <Schedule />
      <Membership />
      <About />
      <Vision />
      <Community />
      <PersonalTraining />
      <Contact />
    </main>
  );
}
